import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Clock, Phone, Save, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: 'opening_hours_mon_fri', label: 'Mo – Fr', placeholder: '08:00 - 17:00', group: 'hours' },
  { key: 'opening_hours_sat', label: 'Samstag', placeholder: 'geschlossen', group: 'hours' },
  { key: 'opening_hours_sun', label: 'Sonntag', placeholder: 'geschlossen', group: 'hours' },
  { key: 'opening_hours_timezone', label: 'Zeitzone', placeholder: 'Europe/Berlin', group: 'hours' },
  { key: 'contact_phone', label: 'Telefonnummer', placeholder: '+49 172 8116925', group: 'phone' },
  { key: 'google_business_url', label: 'Link zum Google-Unternehmensprofil', placeholder: 'https://maps.app.goo.gl/…', group: 'google' },
  { key: 'google_review_url', label: 'Link „Bewertung schreiben“', placeholder: 'https://g.page/r/…/review', group: 'google' },
];

function Card({ icon: Icon, title, hint, group, values, update, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Icon className="w-5 h-5 text-[#066ce7]" /></div>
        <div>
          <h2 className="text-base font-semibold text-[#0F172A]">{title}</h2>
          <p className="text-xs text-slate-500">{hint}</p>
        </div>
      </div>
      <div className="space-y-4">
        {FIELDS.filter(f => f.group === group).map(f => (
          <div key={f.key}>
            <Label className="text-xs">{f.label}</Label>
            <Input value={values[f.key] ?? ''} onChange={e => update(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`setting-${f.key}`} />
          </div>
        ))}
        {children}
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/admin/settings`, { headers }).then(r => setValues(prev => ({ ...(r.data || {}), ...prev }))).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const update = (key, value) => setValues(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    setLoading(true);
    try {
      await Promise.all(FIELDS.map(f => axios.put(`${API}/admin/settings`, { key: f.key, value: (values[f.key] ?? '').trim() }, { headers })));
      toast.success('Einstellungen gespeichert');
    } catch {
      toast.error('Fehler beim Speichern');
    }
    setLoading(false);
  };

  const cardProps = { values, update };
  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0F172A]">Einstellungen</h1>
        <p className="text-sm text-slate-500 mt-1">Globale Website-Einstellungen verwalten</p>
      </div>

      <div className="space-y-6 max-w-2xl">
        <Card icon={Clock} title="Öffnungszeiten" hint="Angezeigt auf der Kontaktseite" group="hours" {...cardProps}>
          <p className="text-xs text-slate-500">Tipp: Für geschlossene Tage einfach „geschlossen" eintragen. Zeitzone gemäß IANA-Standard (z.B. Europe/Berlin).</p>
        </Card>
        <Card icon={Phone} title="Kontaktdaten" hint="Telefonnummer für Footer und Kontaktseite" group="phone" {...cardProps} />
        <Card icon={MapPin} title="Google-Unternehmensprofil" hint="Profil-Link und Bewertungs-Button auf Kontaktseite und im Footer" group="google" {...cardProps}>
          <p className="text-xs text-slate-500" data-testid="setting-google-hint">Solange kein Profil-Link eingetragen ist, verlinkt die Website auf Google Maps mit Ihrer Adresse. Der Bewertungs-Button erscheint erst, wenn ein Bewertungslink hinterlegt ist (Google-Profil → „Rezensionen erhalten“ → Link kopieren).</p>
        </Card>

        <Button onClick={save} disabled={loading} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" /> {loading ? 'Speichern...' : 'Speichern'}
        </Button>
      </div>
    </div>
  );
}
